import { Card } from "@affinity/components/Card";
import { formatAddress, toDate } from "@affinity/constants/FormatHelpers";
import { colours } from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import OrderCompleteInitialise, { OrderCompleteInitialseResults } from "@affinity/Handlers/OrderCompleteInitialise";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productPriceStyles } from "@insite/content-library/Components/ProductPrice";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

// Fields Keys Enumerator
const enum fields {
    errorMessage = "errorMessage",
}

// CMS Properties
interface OwnProps extends WidgetProps {
    fields: {
        [fields.errorMessage]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const billToId = state.context.session.billToId ?? "";
    return {
        billToId,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    OrderCompleteInitialise,
};

const OrderCard = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${styleHelpers.rem(10)};
    padding: ${styleHelpers.rem(20)};
    width: 100%;
    background-color: ${colours.lightGreen};
    color: ${colours.black};
    border-bottom: 3px solid ${colours.green};
    border-radius: 5px;
    @media screen and (min-width: ${styleHelpers.breakpointWidth("lg")}) {
        border-radius: 15px;
        border-bottom-width: 8px;
        padding: ${styleHelpers.rem(40)};
        font-size: ${styleHelpers.rem(18)};
    }
    ul {
        list-style: disc;
        padding-left: 1em;
    }
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const OrderConfirmationOrderRef = ({ billToId, location, OrderCompleteInitialise, fields }: Props) => {
    const [quantity, setQuantity] = useState(0);
    const [productName, setProductName] = useState("");
    const [delivery, setDelivery] = useState("");
    const [address, setAddress] = useState("");
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const onComplete = ({ quantity, cart, delivery, address }: Partial<OrderCompleteInitialseResults>) => {
        setQuantity(quantity ?? 0);
        const item = cart?.cartLines?.[0];
        setProductName(item?.productName || item?.shortDescription || item?.altText || "Fuel");
        setDelivery(toDate(delivery));
        setAddress(formatAddress(address));
        setIsLoading(false);
    };

    const onError = (props: OrderCompleteInitialseResults) => {
        setHasError(true);
        setIsLoading(false);
    };

    useEffect(() => {
        const parsedQuery = parseQueryString<{ cartId?: string }>(location.search);
        const cartId = parsedQuery.cartId!;
        OrderCompleteInitialise({ cartId, billToId, onComplete, onError });
    }, []);

    return (
        <>
            {isLoading && (
                <OrderCard>
                    <LoadingSpinner
                        css={css`
                            align-self: center;
                        `}
                    />
                </OrderCard>
            )}
            {!hasError && !isLoading && (
                <OrderCard>
                    <Typography variant="h2">We have successfully received your order for:</Typography>
                    <ul>
                        <li>
                            {productName}, {quantity} litres
                        </li>
                        <li>Delivery {delivery}</li>
                        <li>Delivered to {address}</li>
                    </ul>
                </OrderCard>
            )}
            {hasError && !isLoading && <OrderCard>{fields.errorMessage}</OrderCard>}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderConfirmationOrderRef),
    definition: {
        displayName: "Order Number Ref",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
        fieldDefinitions: [
            {
                name: fields.errorMessage,
                displayName: "Error Message",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                isRequired: true,
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
